import { useCallback, useEffect, useState } from "react";
import { GiPlantRoots } from "react-icons/gi";
import { LuCopyPlus } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroCultura from "../../components/modais/ModalCadastroCultura";
import ModalCadastroFazenda from "../../components/modais/ModalCadastroFazenda";
import ModalCadastroSafra from "../../components/modais/ModalCadastroSafra";
import ModalCadastroTalhao from "../../components/modais/ModalCadastroTalhao";
import { repositorioCultura, repositorioPlantio, repositorioSafra } from "../../sync/repositories/agricultura";
import { repositorioFazenda, repositorioTalhao } from "../../sync/repositories/fazendas";

const AJUDA = {
  titulo: "Cadastro de Plantios",
  descricao:
    "Um Plantio registra que uma determinada Cultura foi plantada em um Talhão, dentro de uma Safra específica. É o elo central da produção: a partir do plantio é que depois se lançam as Colheitas, então cadastre-o assim que a operação de plantio for realizada em campo.",
  secoes: [
    {
      titulo: "Dados do Plantio",
      campos: [
        {
          nome: "Fazenda",
          obrigatorio: true,
          explicacao: "A fazenda onde o plantio está sendo realizado. Se ainda não existir, use o botão '+' ao lado do campo para cadastrá-la sem sair desta tela.",
        },
        {
          nome: "Cultura",
          obrigatorio: true,
          explicacao: "A espécie plantada (ex.: Soja, Milho). Precisa existir no cadastro de Culturas antes de ser usada aqui.",
        },
        {
          nome: "Safra",
          obrigatorio: true,
          explicacao: "O período de cultivo ao qual esse plantio pertence (ex.: 'Safra 2024/2025'). Precisa existir no cadastro de Safras antes de ser usada aqui.",
        },
        {
          nome: "Talhão",
          obrigatorio: true,
          explicacao: "A área da fazenda onde o plantio foi feito. Precisa existir no cadastro de Talhões antes de ser usada aqui.",
        },
        {
          nome: "Área (hectares)",
          obrigatorio: true,
          explicacao: "Quantos hectares desse talhão foram efetivamente plantados com essa cultura/safra. Serve de referência para cálculo de produtividade quando a colheita for lançada.",
        },
        {
          nome: "Data do Plantio",
          obrigatorio: true,
          explicacao: "Data em que o plantio foi realizado em campo.",
        },
        {
          nome: "Descrição / Observações",
          obrigatorio: false,
          explicacao: "Informações complementares como variedade utilizada, espaçamento entre linhas, condições do solo ou do clima no dia do plantio.",
        },
      ],
    },
  ],
  dicas: [
    "Um plantio com colheitas vinculadas pode não ser excluído — verifique se não há registros vinculados antes de tentar remover.",
  ],
};

export default function GestaoPlantios() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [plantios, setPlantios] = useState([]);
  const [plantiosFiltrados, setPlantiosFiltrados] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [mostrarModalFazenda, setMostrarModalFazenda] = useState(false);
  const [mostrarModalCultura, setMostrarModalCultura] = useState(false);
  const [mostrarModalSafra, setMostrarModalSafra] = useState(false);
  const [mostrarModalTalhao, setMostrarModalTalhao] = useState(false);
  
  // Dados para os selects
  const [culturas, setCulturas] = useState([]);
  const [safras, setSafras] = useState([]);
  const [talhoes, setTalhoes] = useState([]);
  const [fazendas, setFazendas] = useState([]);
  
  const [filtros, setFiltros] = useState({
    busca: ""
  });
  
  const [formData, setFormData] = useState({
    fazenda: "",
    cultura: "",
    safra: "",
    talhao: "",
    area_hectares: "",
    data_plantio: "",
    descricao: "",
  });

  // Carregar dados dos selects
  const carregarCulturas = async () => {
    try {
      const response = await repositorioCultura.listar();
      setCulturas(response.data.results || response.data);
    } catch (error) {
      console.error("Erro ao carregar culturas:", error);
    }
  };

  const carregarSafras = async () => {
    try {
      const response = await repositorioSafra.listar();
      setSafras(response.data.results || response.data);
    } catch (error) {
      console.error("Erro ao carregar safras:", error);
    }
  };

  const carregarTalhoes = async () => {
    try {
      const response = await repositorioTalhao.listar();
      setTalhoes(response.data.results || response.data);
    } catch (error) {
      console.error("Erro ao carregar talhões:", error);
    }
  };

  const carregarFazendas = async () => {
    try {
      const response = await repositorioFazenda.listar();
      setFazendas(response.data.results || response.data);
    } catch (error) {
      console.error("Erro ao carregar fazendas:", error);
    }
  };

  // Carregar plantios
  const carregarPlantios = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioPlantio.listar();
      const dados = response.data.results || response.data;
      setPlantios(dados);
      setPlantiosFiltrados(dados);
    } catch (error) {
      console.error("Erro ao carregar plantios:", error);
      alert("Erro ao carregar lista de plantios");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarPlantios();
    carregarCulturas();
    carregarSafras();
    carregarTalhoes();
    carregarFazendas();
  }, [carregarPlantios]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.fazenda) {
      alert("Selecione uma fazenda.");
      return false;
    }
    if (!formData.cultura) {
      alert("Selecione uma cultura.");
      return false;
    }
    if (!formData.safra) {
      alert("Selecione uma safra.");
      return false;
    }
    if (!formData.talhao) {
      alert("Selecione um talhão.");
      return false;
    }
    if (!formData.area_hectares || Number(formData.area_hectares) <= 0) {
      alert("Informe uma área válida.");
      return false;
    }
    if (!formData.data_plantio) {
      alert("Informe a data do plantio.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      fazenda: formData.fazenda,
      cultura: formData.cultura,
      safra: formData.safra,
      talhao: formData.talhao,
      area_hectares: Number(formData.area_hectares),
      data_plantio: formData.data_plantio,
      descricao: formData.descricao || "",
    };

    try {
      if (editando) {
        const response = await repositorioPlantio.atualizar(editandoId, payload);
        if (response.status === 200) {
          alert("Plantio atualizado com sucesso!");
          handleCancel();
          carregarPlantios();
        }
      } else {
        const response = await repositorioPlantio.criar(payload);
        if (response.status === 201 || response.status === 200) {
          alert("Plantio cadastrado com sucesso!");
          handleCancel();
          carregarPlantios();
        }
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (plantio) => {
    setFormData({
      fazenda: plantio.fazenda?.id || plantio.fazenda || "",
      cultura: plantio.cultura?.id || plantio.cultura || "",
      safra: plantio.safra?.id || plantio.safra || "",
      talhao: plantio.talhao?.id || plantio.talhao || "",
      area_hectares: plantio.area_hectares || "",
      data_plantio: plantio.data_plantio || "",
      descricao: plantio.descricao || "",
    });
    setEditando(true);
    setEditandoId(plantio.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovoPlantio = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      fazenda: "",
      cultura: "",
      safra: "",
      talhao: "",
      area_hectares: "",
      data_plantio: "",
      descricao: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este plantio? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioPlantio.excluir(id);
      alert("Plantio excluído com sucesso!");
      carregarPlantios();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir plantio. Verifique se não há registros vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...plantios];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(p => 
        p.cultura_nome?.toLowerCase().includes(buscaLower) ||
        p.safra_nome?.toLowerCase().includes(buscaLower) ||
        p.talhao_nome?.toLowerCase().includes(buscaLower) ||
        p.fazenda_nome?.toLowerCase().includes(buscaLower)
      );
    }
    
    setPlantiosFiltrados(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "" });
    setPlantiosFiltrados(plantios);
  };

  // Formatar área
  const formatarArea = (area) => {
    if (!area && area !== 0) return "—";
    return `${Number(area).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha`;
  };

  // Formatar data
  const formatarData = (data) => {
    if (!data) return "—";
    return new Date(data).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  // Truncar descrição
  const truncarDescricao = (texto, maxLength = 60) => {
    if (!texto) return "—";
    if (texto.length <= maxLength) return texto;
    return texto.substring(0, maxLength) + "...";
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Plantios <GiPlantRoots size={38}/></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoPlantio}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Plantio
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Plantio" : "Cadastrar Novo Plantio"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Linha 1: Fazenda, Cultura, Safra */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Fazenda *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="fazenda"
                        value={formData.fazenda}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione uma fazenda</option>
                        {fazendas.map(f => (
                          <option key={f.id} value={f.id}>{f.nome}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalFazenda(true)}
                        title="Cadastrar nova fazenda"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Cultura *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="cultura"
                        value={formData.cultura}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione uma cultura</option>
                        {culturas.map(c => (
                          <option key={c.id} value={c.id}>{c.nome}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalCultura(true)}
                        title="Cadastrar nova cultura"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Safra *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="safra"
                        value={formData.safra}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione uma safra</option>
                        {safras.map(s => (
                          <option key={s.id} value={s.id}>{s.nome}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalSafra(true)}
                        title="Cadastrar nova safra"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Linha 2: Talhão, Área, Data */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Talhão *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="talhao"
                        value={formData.talhao}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione um talhão</option>
                        {talhoes.map(t => (
                          <option key={t.id} value={t.id}>{t.nome_codigo}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalTalhao(true)}
                        title="Cadastrar novo talhão"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Área (hectares) *</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        name="area_hectares"
                        value={formData.area_hectares}
                        onChange={handleChange}
                        className="w-full p-2 rounded-l focus:ring-2 focus:ring-green-300"
                        step="0.01"
                        min="0.01"
                        placeholder="0,00"
                        required
                      />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">ha</span>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Data do Plantio *</label>
                    <input
                      type="date"
                      name="data_plantio"
                      value={formData.data_plantio}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    />
                  </div>
                </div>

                {/* Linha 3: Descrição */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Descrição / Observações</label>
                  <textarea
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleChange}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    rows="4"
                    placeholder="Informações adicionais sobre o plantio (variedade, espaçamento, condições, etc.)..."
                  />
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Cultura, Safra, Talhão ou Fazenda</label>
                  <input
                    type="text"
                    placeholder="Digite o nome da cultura, safra, talhão ou fazenda..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Plantios */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {plantiosFiltrados.length} plantio(s) encontrado(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Cultura</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Safra</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Talhão</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Fazenda</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Área</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Data Plantio</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Descrição</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plantiosFiltrados.map((plantio) => (
                        <tr key={plantio.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{plantio.id}</td>
                          <td className="p-3 text-sm font-medium">{plantio.cultura_nome || "-"}</td>
                          <td className="p-3 text-sm">{plantio.safra_nome || "-"}</td>
                          <td className="p-3 text-sm">{plantio.talhao_nome || "-"}</td>
                          <td className="p-3 text-sm">{plantio.fazenda_nome || "-"}</td>
                          <td className="p-3 text-sm">{formatarArea(plantio.area_hectares)}</td>
                          <td className="p-3 text-sm">{formatarData(plantio.data_plantio)}</td>
                          <td className="p-3 text-sm max-w-xs">{truncarDescricao(plantio.descricao)}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(plantio)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(plantio.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Excluir"
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      
                      {plantiosFiltrados.length === 0 && (
                        <tr>
                          <td colSpan="9" className="p-8 text-center text-gray-500">
                            {filtros.busca 
                              ? "Nenhum plantio encontrado com os filtros selecionados."
                              : "Nenhum plantio cadastrado."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <ModalCadastroFazenda
        isOpen={mostrarModalFazenda}
        onClose={() => setMostrarModalFazenda(false)}
        onSuccess={async (nova) => {
          await carregarFazendas();
          setFormData((prev) => ({ ...prev, fazenda: nova.id }));
          setMostrarModalFazenda(false);
        }}
      />
      <ModalCadastroCultura
        isOpen={mostrarModalCultura}
        onClose={() => setMostrarModalCultura(false)}
        onSuccess={async (nova) => {
          await carregarCulturas();
          setFormData((prev) => ({ ...prev, cultura: nova.id }));
        }}
      />
      <ModalCadastroSafra
        isOpen={mostrarModalSafra}
        onClose={() => setMostrarModalSafra(false)}
        onSuccess={async (nova) => {
          await carregarSafras();
          setFormData((prev) => ({ ...prev, safra: nova.id }));
        }}
      />
      <ModalCadastroTalhao
        isOpen={mostrarModalTalhao}
        onClose={() => setMostrarModalTalhao(false)}
        fazendaSelecionadaId={formData.fazenda}
        onSuccess={async (novo) => {
          await carregarTalhoes();
          setFormData((prev) => ({ ...prev, talhao: novo.id }));
        }}
      />
    </Layout>
  );
}